// Pure-data TOC tree, split out from TOCLayers.ts so repo-root scripts (e.g.
// scripts/generateLayerConfigFixture.ts) can import it without pulling in
// the runtime side effects of ./layers. TOCLayers.ts re-exports `TOC` from here.

import type { TOC as TOCType } from '~/common/types';
import { digitizationTOC } from './digitizationLayers';

export const TOC: TOCType = [
  {
    // Project-scoped, client-rendered GeoJSON overlays (app rows; see
    // scripts/generateLayerConfigFixture.ts APP_LAYER_KEYS). Rendered by the dedicated
    // FieldNotesLayer / PotholesLayer components, not the tile loop in MapLayers. Kept FIRST
    // so it's the top catalog section (below Bookmarked / Linked Projects, above Utilities).
    label: 'Site Data',
    expanded: true,
    children: [
      {
        id: 'fieldNotes',
        label: 'Field Notes',
      },
      {
        id: 'potholes',
        label: 'Potholes',
      },
    ],
  },
  {
    label: 'Utilities',
    expanded: true,
    children: [
      {
        label: 'Electric',
        expanded: true,
        children: [
          {
            id: 'electricTransmissionLines',
            label: 'Transmission Lines',
          },
          {
            id: 'electricTransmissionStructures',
            label: 'Transmission Structures',
          },
          {
            id: 'electricDistributionLines',
            label: 'Distribution Lines',
          },
          {
            id: 'electricDistributionStructures',
            label: 'Distribution Structures',
          },
          {
            id: 'electricDistributionDevices',
            label: 'Distribution Devices',
          },
          {
            id: 'electricDistributionServiceLines',
            label: 'Service Lines',
          },
          {
            id: 'electricDistributionServiceVaults',
            label: 'Vaults',
          },
          {
            id: 'electricDistributionStreetLights',
            label: 'Street Lights',
          },
          {
            id: 'electricDistributionOther',
            label: 'Other Assets',
          },
        ],
      },
      {
        label: 'Gas',
        expanded: true,
        children: [
          {
            id: 'gasTransmission',
            label: 'Transmission Lines',
          },
          {
            id: 'gasDistribution',
            label: 'Distribution Main Lines',
          },
          {
            id: 'gasService',
            label: 'Services Lines',
          },
          {
            id: 'gasPoints',
            label: 'Point Assets',
          },
          {
            id: 'gasDistanceArrow',
            label: 'PG&E Gas Distance Notes',
          },
        ],
      },
      {
        label: 'Oil & Gas',
        expanded: true,
        children: [
          {
            id: 'fossilFuelPipelines',
            label: 'Fossil Fuel Pipelines',
          },
        ],
      },
      {
        label: 'Water',
        expanded: true,
        children: [
          {
            id: 'waterTransmission',
            label: 'Transmission Lines',
          },
          {
            id: 'waterMain',
            label: 'Main Lines',
          },
          {
            id: 'waterService',
            label: 'Service Lines',
          },
          {
            id: 'waterPoints',
            label: 'Point Assets',
          },
        ],
      },
      {
        label: 'Sewer',
        expanded: true,
        children: [
          {
            id: 'sanitaryPipes',
            label: 'Sewer Lines',
          },
          {
            id: 'sanitaryPoints',
            label: 'Point Assets',
          },
        ],
      },
      {
        label: 'Storm',
        expanded: true,
        children: [
          {
            id: 'stormPipes',
            label: 'Storm Lines',
          },
          {
            id: 'stormPoints',
            label: 'Point Assets',
          },
        ],
      },
      {
        label: 'Recycled Water',
        expanded: true,
        children: [
          {
            id: 'recycledWaterPipes',
            label: 'Recycled Water Lines',
          },
          {
            id: 'recycledWaterPoints',
            label: 'Point Assets',
          },
        ],
      },
      {
        label: 'Telco',
        expanded: true,
        children: [
          {
            id: 'fiberRoutes',
            label: 'Fiber Lines and Routes',
          },
          {
            id: 'fiberLitBuildings',
            label: 'Fiber Lit Buildings',
          },
          {
            id: 'cellTowers',
            label: 'Cell Towers',
          },

          {
            id: 'submarineCables',
            label: 'Submarine Lines and Routes',
          },
          {
            id: 'submarineLandings',
            label: 'Submarine Landings',
          },
          {
            id: 'fccPoints',
            label: 'FCC Data',
          },
          {
            id: 'antennaSearch',
            label: 'Antennas',
          },
        ],
      },
      ...digitizationTOC,
    ],
  },
  {
    label: 'Land Rights',
    expanded: true,
    children: [
      {
        id: 'landRightsParcels',
        label: 'Parcels',
      },
      {
        id: 'pgeParcelBasemap',
        label: 'PG&E Parcel Basemap',
      },
      {
        id: 'landRightsEasements',
        label: 'Easements (beta)',
      },
    ],
  },
  {
    label: 'Topo',
    expanded: true,
    children: [
      {
        id: 'contourLines3ft',
        label: 'Contour Lines (3 ft)',
      },
    ],
  },
  {
    label: 'Geotechnical',
    expanded: true,
    children: [
      {
        id: 'geotechnicalStudies',
        label: 'Geotechnical Studies',
      },
      {
        id: 'soils',
        label: 'NRCS gSSURGO Soil Survey',
      },
      {
        id: 'geotechnicalGeologySGMC',
        label: 'State Geologic Map (SGMC)',
      },
      {
        label: 'Seismic',
        expanded: true,
        children: [
          {
            id: 'geotechnicalAlquistPrioloFaultHazardZones',
            label: 'Alquist Priolo Fault Hazard Zones',
          },
          {
            id: 'geotechnicalFaultLines',
            label: 'Fault Lines',
          },
          {
            id: 'geotechnicalLandslideAreas',
            label: 'Landslide Areas',
          },
          {
            id: 'geotechnicalLiquefactionZones',
            label: 'Liquefaction Zones',
          },
        ],
      },
    ],
  },
  {
    label: 'Boundaries',
    expanded: true,
    children: [
      {
        id: 'coreBasedStatisticalAreas',
        label: 'Core Based Statistical Areas',
      },
      {
        id: 'city',
        label: 'Cities',
      },
      {
        id: 'county',
        label: 'Counties',
      },
      {
        id: 'urbanAreaBoundaries',
        label: 'Urban/Rural Boundaries',
      },
      {
        id: 'countySubdivision',
        label: 'County Subdivisions',
      },
      {
        id: 'incorporatedPlaces',
        label: 'Incorporated Places',
      },
      {
        id: 'unIncorporatedPlaces',
        label: 'Unincorporated Places',
      },
      {
        id: 'environmentalFederalLands',
        label: 'Federal Lands',
      },
      {
        id: 'californiaStateParkBoundaries',
        label: 'California State Parks',
      },
      {
        id: 'federallyRecognizedTribalLands',
        label: 'Federally Recognized Tribal Lands',
      },
      {
        id: 'newYorkStateParkBoundaries',
        label: 'New York State Parks',
      },
      {
        id: 'newYorkCityParks',
        label: 'New York City Parks',
      },
      {
        id: 'newYorkDisadvantagedCommunities',
        label: 'New York Disadvantaged Communities',
      },
    ],
  },
  {
    label: 'Land Use',
    expanded: true,
    children: [
      {
        id: 'landUseZoningDataAvailability',
        label: 'Zoning Data Availability',
      },
      {
        id: 'buildings',
        label: 'Building Footprints',
      },
      {
        id: 'landUseNationalLandCoverDatabase',
        label: 'National Land Cover Database',
      },
      {
        id: 'otherCrops',
        label: 'Crops',
      },
      {
        id: 'otherFarmlandMonitoring',
        label: 'Farmland Monitoring and Mapping',
      },
      {
        label: 'Civic',
        expanded: true,
        children: [
          {
            id: 'otherHospitals',
            label: 'Hospitals',
          },
          {
            id: 'otherK12Schools',
            label: 'K-12 Schools',
          },
          {
            id: 'otherCollegesUniversities',
            label: 'Colleges and Universities',
          },
          {
            id: 'otherCSCDSchools',
            label: 'CSCD Schools',
          },
          {
            id: 'otherCSCDUniversityLands',
            label: 'CSCD University Lands',
          },
        ],
      },
    ],
  },
  {
    label: 'Transportation',
    expanded: true,
    children: [
      {
        label: 'Roads',
        expanded: true,
        children: [
          {
            id: 'landUseNationalHighway',
            label: 'National Highway Network',
          },
        ],
      },
      {
        label: 'State DOT',
        expanded: true,
        children: [
          {
            id: 'landUseStateHighway',
            label: 'State Highway Network',
          },
          {
            id: 'landUseTransportationDistrictBoundaries',
            label: 'Transportation District Boundaries',
          },
          {
            id: 'landUseRightOfWay',
            label: 'Right Of Way',
          },
          {
            id: 'landUsePostmiles',
            label: 'Postmiles',
          },
          {
            id: 'landUseStateHighwayBridges',
            label: 'State Highway Bridges',
          },
        ],
      },
      {
        label: 'Transit',
        expanded: true,
        children: [
          {
            id: 'transitLines',
            label: 'Transit Lines',
          },
          {
            id: 'transitStops',
            label: 'Transit Stops',
          },
        ],
      },
      {
        id: 'landUseRailroads',
        label: 'Railroads',
      },
      {
        id: 'transitAirports',
        label: 'Airports',
      },
      {
        id: 'landUseCurbData',
        label: 'Curbs (San Francisco)',
      },
    ],
  },
  {
    label: 'Infrastructure',
    expanded: true,
    children: [
      {
        label: 'Energy',
        expanded: true,
        children: [
          {
            id: 'geotechnicalOilGasWells',
            label: 'Oil and Gas Wells',
          },
          {
            id: 'geotechnicalOilGasRefineriesProcessingPlants',
            label: 'Oil and Gas Refineries and Processing Plants',
          },
          {
            id: 'geotechnicalPowerPlants',
            label: 'Power Plants',
          },
        ],
      },
      {
        label: 'Flood Control',
        expanded: true,
        children: [
          {
            id: 'geotechnicalLeveeAreas',
            label: 'Leveed Areas',
          },
          {
            id: 'geotechnicalLeveeSystemLines',
            label: 'Levee System Lines',
          },
          {
            id: 'geotechnicalLeveeSystemPoints',
            label: 'Levee System Points',
          },
          {
            id: 'otherJurisdictionalDams',
            label: 'Jurisdictional Dams',
          },
        ],
      },
    ],
  },
  {
    label: 'Environmental',
    expanded: true,
    children: [
      {
        label: 'Contaminated Sites',
        expanded: true,
        children: [
          {
            id: 'geotechnicalESAStudies',
            label: 'ESA Studies',
          },
        ],
      },
      {
        label: 'Contaminated Sites - CA',
        expanded: true,
        children: [
          {
            id: 'environmentalHazardousWaste',
            label: 'DTSC EnviroStor Sites',
          },
          {
            id: 'environmentalGeoTrackerSites',
            label: 'SWRCB GeoTracker Sites',
          },
        ],
      },
      {
        id: 'floridaEnvironmentalCleanUpSites',
        label: 'Clean-up Sites - FL',
      },
      {
        label: 'Ecology',
        expanded: true,
        children: [
          {
            id: 'environmentalUSFWSEndangeredThreatenedSpecies',
            label: 'USFWS Endangered/Threatened Species',
          },
          {
            id: 'environmentalUSFWSCriticalHabitat',
            label: 'USFWS Critical Habitat',
          },
        ],
      },
      {
        id: 'floridaEnvironmentalESIShoreline',
        label: 'Environmental Sensitivity Index (ESI) Shoreline',
      },
      {
        id: 'floridaEnvironmentalExistingERPPermits',
        label: 'Existing ERP Permits - FL',
      },
      {
        id: 'floridaEnvironmentalICRSites',
        label: 'ICR Sites - FL',
      },
      {
        label: 'Protected Areas - Federal',
        expanded: true,
        children: [
          {
            id: 'exclusionUSGSProtectedAreas',
            label: 'USGS Protected Areas Database',
          },
          {
            id: 'environmentalLCWFPolygons',
            label: 'Section 6(f) LWCF Parcels',
          },
          {
            id: 'environmentalLCWFPoints',
            label: 'Section 6(f) LWCF Points',
          },
          {
            id: 'environmentalMarineProtected',
            label: 'NOAA Marine Protected Areas',
          },
        ],
      },
      {
        label: 'Protected Areas - CA',
        expanded: true,
        children: [
          {
            id: 'environmentalCEHCPEssentialConnectivity',
            label: 'CEHCP Essential Connectivity Areas',
          },
          {
            id: 'environmentalCEHCPTerrestrialConnectivity',
            label: 'CEHCP Terrestrial Connectivity',
          },
          {
            id: 'exclusionLandHeldInConservationEasementCCED',
            label: 'CCED Lands Held in Conservation Easement',
          },
          {
            id: 'exclusionProtectedAreaDatabaseHoldingsCPAD',
            label: 'CPAD Protected Area Database Holdings',
          },
          {
            id: 'environmentalBayAreaEssentialLandsLinkage',
            label: 'Bay Area Essential Lands Linkage Design',
          },
          {
            id: 'environmentalSacramentoProtectedTrees',
            label: 'Sacramento Protected Trees',
          },
        ],
      },
      {
        label: 'Protected Areas - FL',
        expanded: true,
        children: [
          {
            id: 'floridaExclusionConservationLands',
            label: 'Conservation Lands',
          },
          {
            id: 'floridaEnvironmentalCriticalWildlifeArea',
            label: 'Critical Wildlife Areas',
          },
          {
            id: 'floridaGeotechnicalGroundWaterContaminationAreas',
            label: 'Ground Water Contamination Areas',
          },
          {
            id: 'floridaEnvironmentalManagedLands',
            label: 'Managed Lands',
          },
        ],
      },
      {
        id: 'environmentalTransportationNoise',
        label: 'USDOT Transportation Noise',
      },
    ],
  },
  {
    label: 'Climate',
    expanded: true,
    children: [
      {
        label: 'Sea Level Rise',
        expanded: true,
        children: [
          {
            id: 'environmentalCaltransHighwaysExposure0_50SLR',
            label: 'Caltrans Highways Exposure to 0.50m SLR',
          },
          {
            id: 'environmentalCaltransHighwaysExposure1_00SLR',
            label: 'Caltrans Highways Exposure to 1.00m SLR',
          },
          {
            id: 'environmentalCaltransHighwaysExposure1_75SLR',
            label: 'Caltrans Highways Exposure to 1.75m SLR',
          },
        ],
      },
      {
        label: 'Future Temperature Changes',
        expanded: true,
        children: [
          {
            id: 'environmentalChangeInAverageMinimumTemperature2025',
            label: 'Change in Average Minimum Temperature in 2025',
          },
          {
            id: 'environmentalChangeInAverageMinimumTemperature2055',
            label: 'Change in Average Minimum Temperature in 2055',
          },
          {
            id: 'environmentalChangeInAverageMinimumTemperature2085',
            label: 'Change in Average Minimum Temperature in 2085',
          },
          {
            id: 'environmentalChangeInAverageMaximumTemperature2025',
            label: 'Change in Average Maximum Temperature in 2025',
          },
          {
            id: 'environmentalChangeInAverageMaximumTemperature2055',
            label: 'Change in Average Maximum Temperature in 2055',
          },
          {
            id: 'environmentalChangeInAverageMaximumTemperature2085',
            label: 'Change in Average Maximum Temperature in 2085',
          },
        ],
      },
      {
        label: 'Wildfires',
        expanded: true,
        children: [
          {
            id: 'environmentalCPUCHighFireHazardZonesTier1',
            label: 'CPUC High Fire Hazard Zones - Tier 1',
          },
          {
            id: 'environmentalCPUCHighThreatDistricts',
            label: 'CPUC Fire Threat Districts',
          },
        ],
      },
      {
        label: 'Wind Resources',
        expanded: true,
        children: [
          {
            id: 'windPotentialWindCapacity140',
            label: 'Potential Wind Capacity: 140m Hub Height',
          },
          {
            id: 'windPotentialWindCapacity110',
            label: 'Potential Wind Capacity: 110m Hub Height',
          },
          {
            id: 'windPotentialWindCapacity80',
            label: 'Potential Wind Capacity: 80m Hub Height',
          },
        ],
      },
      {
        label: 'Solar Resources',
        expanded: true,
        children: [
          {
            id: 'solarPhotovoltaic',
            label: 'Solar Photovoltaic (Wh/m2/day)',
          },
          {
            id: 'solar2019DirectNormalIrridance',
            label: 'National Solar Radiation Database - Direct Normal Irradiance',
          },
        ],
      },
    ],
  },
  {
    label: 'Hydrology',
    expanded: true,
    children: [
      {
        id: 'streams',
        label: 'Streams',
      },
      {
        id: 'waterbodies',
        label: 'Waterbodies',
      },
      {
        id: 'geotechnicalGroundwaterStations',
        label: 'Groundwater Stations',
      },
      {
        id: 'geotechnicalGroundwaterElevations',
        label: 'Groundwater Elevations',
      },
      {
        id: 'hydrologicRegions',
        label: 'Hydrologic Regions',
      },
      {
        id: 'environmentalWatersheds',
        label: 'Watersheds',
      },
      {
        id: 'environmentalWetlands',
        label: 'Wetlands',
      },
      {
        id: 'environmentalWetlandsNewYork',
        label: 'Wetlands - New York',
      },
      {
        id: 'environmentalCurrentDroughtConditions',
        label: 'Current Drought Conditions',
      },
      {
        label: 'FEMA Flood Zones',
        expanded: true,
        children: [
          {
            id: 'geotechnicalFloodHazardZones',
            label: 'Flood Hazard Zones',
          },
          {
            id: 'geotechnicalCrossSections',
            label: 'Cross Sections',
          },
          {
            id: 'geotechnicalCoastalTransects',
            label: 'Coastal Transects',
          },
          {
            id: 'geotechnicalBaseFloodElevations',
            label: 'Base Flood Elevations',
          },
        ],
      },
      {
        label: 'Regulated Waters - CA',
        expanded: true,
        children: [
          {
            id: 'environmentalDesignatedFloodway',
            label: 'CA DWR Regulated Floodway',
          },
          {
            id: 'environmentalHighRiskWatershedsSWRCB',
            label: 'SWRCB High Risk Receiving Watersheds',
          },
        ],
      },
      {
        label: 'Regulated Waters - National',
        expanded: true,
        children: [
          {
            id: 'environmental303dImpairedWaters',
            label: '303(d) Impaired Waters',
          },
          {
            id: 'environmentalRegulatedStreams',
            label: 'USGS Regulated Streams',
          },
        ],
      },
      {
        label: 'Precipitation',
        expanded: true,
        children: [
          {
            id: 'precipitationNOAAWeatherStations',
            label: 'NOAA Weather Stations',
          },
          {
            id: 'precipitation100YearRainfall60Minutes',
            label: '100 Year Rainfall - 60 Minutes (in.)',
          },
          {
            id: 'precipitation100YearRainfall24Hours',
            label: '100 Year Rainfall - 24 Hours (in.)',
          },
          {
            id: 'precipitationAverageAnnual',
            label: 'Average Annual Precipitation (in.)',
          },
          {
            id: 'precipitationJanuaryAverageAnnual',
            label: 'January Average Precipitation (in.)',
          },
          {
            id: 'precipitationFebruaryAverageAnnual',
            label: 'February Average Precipitation (in.)',
          },
          {
            id: 'precipitationMarchAverageAnnual',
            label: 'March Average Precipitation (in.)',
          },
          {
            id: 'precipitationAprilAverageAnnual',
            label: 'April Average Precipitation (in.)',
          },
          {
            id: 'precipitationMayAverageAnnual',
            label: 'May Average Precipitation (in.)',
          },
          {
            id: 'precipitationJuneAverageAnnual',
            label: 'June Average Precipitation (in.)',
          },
          {
            id: 'precipitationJulyAverageAnnual',
            label: 'July Average Precipitation (in.)',
          },
          {
            id: 'precipitationAugustAverageAnnual',
            label: 'August Average Precipitation (in.)',
          },
          {
            id: 'precipitationSeptemberAverageAnnual',
            label: 'September Average Precipitation (in.)',
          },
          {
            id: 'precipitationOctoberAverageAnnual',
            label: 'October Average Precipitation (in.)',
          },
          {
            id: 'precipitationNovemberAverageAnnual',
            label: 'November Average Precipitation (in.)',
          },
          {
            id: 'precipitationDecemberAverageAnnual',
            label: 'December Average Precipitation (in.)',
          },
        ],
      },
    ],
  },
  {
    label: 'Coastal',
    expanded: true,
    children: [
      {
        id: 'otherTsunamiInundationAreas',
        label: 'Tsunami Inundation Areas',
      },
      {
        label: 'CA Coastal Commission',
        expanded: true,
        children: [
          {
            id: 'otherCoastalZoneBoundary',
            label: 'Coastal Zone Boundary',
          },
          {
            id: 'environmentalSpecialBiologicalSignificanceAreas',
            label: 'Special Biological Significance Area',
          },
          {
            id: 'environmentalCriticalCoastalAreas',
            label: 'Critical Coastal Areas',
          },
          {
            id: 'environmentalLocalCoastalProgram',
            label: 'Local Coastal Program',
          },
        ],
      },
    ],
  },
  {
    label: 'Survey',
    expanded: true,
    children: [
      {
        id: 'landRightsMonuments',
        label: 'Monuments',
      },
      {
        id: 'surveyBenchmarks',
        label: 'Benchmarks',
      },
      // {
      //   id: 'BENCHMARK_PLACEHOLDER',
      //   label: 'BENCHMARK PLACEHOLDER'
      // },
      {
        id: 'landRightsPLSSSections',
        label: 'PLSS Sections',
      },
      {
        id: 'surveyCoverage',
        label: 'Historical Survey Coverage (beta)',
      },
      {
        id: 'surveyLines',
        label: 'Historical Survey Lines (beta)',
      },
      {
        id: 'surveyPoints',
        label: 'Historical Survey Points (beta)',
      },
      {
        id: 'surveyAnnotations',
        label: 'Historical Survey Annotations (beta)',
      },
    ],
  },
];
